import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';
import HolographicCard from '../../../components/common/HolographicCard.tsx';
import GradientText from '../../../components/common/GradientText.tsx';

interface SecuritySettingsProps {
  settings: {
    twoFactorEnabled: boolean;
    lastPasswordChange: string;
    sessionTimeout: number;
  };
  onUpdate: (type: string, data: any) => Promise<void>;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  settings,
  onUpdate,
}) => {
  const [formData, setFormData] = useState(settings);

  const handleChange = async (key: keyof typeof formData, value: any) => {
    const newData = {
      ...formData,
      [key]: value,
    };
    setFormData(newData);
    await onUpdate('security', newData);
  };

  return (
    <HolographicCard>
      <GradientText variant="h6" gradient="primary" gutterBottom>
        Security Settings
      </GradientText>
      <Stack spacing={3}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.twoFactorEnabled}
              onChange={(e) => handleChange('twoFactorEnabled', e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#8a2be2',
                  '&:hover': {
                    backgroundColor: 'rgba(138, 43, 226, 0.1)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#8a2be2',
                },
              }}
            />
          }
          label={
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Two-Factor Authentication
            </Typography>
          }
        />
        <Box>
          <GradientText variant="subtitle2" gradient="secondary" gutterBottom>
            Last Password Change
          </GradientText>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {new Date(formData.lastPasswordChange).toLocaleDateString()}
          </Typography>
        </Box>
        <Box>
          <GradientText variant="subtitle2" gradient="secondary" gutterBottom>
            Session Timeout (minutes)
          </GradientText>
          <TextField
            type="number"
            value={formData.sessionTimeout}
            onChange={(e) => handleChange('sessionTimeout', Number(e.target.value))}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                },
              },
              '& input': {
                color: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          />
        </Box>
        <Button 
          variant="outlined" 
          sx={{
            borderColor: 'rgba(138, 43, 226, 0.3)',
            color: 'rgba(138, 43, 226, 0.8)',
            '&:hover': {
              borderColor: 'rgba(0, 255, 255, 0.3)',
              color: 'rgba(0, 255, 255, 0.8)',
              backgroundColor: 'rgba(0, 255, 255, 0.1)',
            }
          }}
        >
          Change Password
        </Button>
      </Stack>
    </HolographicCard>
  );
};

export default SecuritySettings; 